import { z } from 'zod';
import { Email, IsoDateTime, Uuid, Version } from './common.js';
/**
 * Accès support à un tenant, consenti par son Owner (ADR-022, phase H7).
 *
 * Un opérateur de la plateforme (`platform.support_access`) **demande** un accès
 * motivé et borné dans le temps ; un utilisateur du tenant portant
 * `support.decide` l'approuve, le refuse ou le révoque ; l'opérateur **entre**
 * alors dans le tenant en lecture seule (permissions `*.read` + transaction
 * PostgreSQL en lecture seule, R-SUP-02) jusqu'à l'expiration, sa sortie ou la
 * révocation. Chaque étape est journalisée dans les deux journaux (R-SUP-04).
 */
/** Statut stocké de la demande. */
export const SupportAccessStatus = z.enum([
    'PENDING',
    'APPROVED',
    'DECLINED',
    'CANCELLED',
    'REVOKED',
]);
/**
 * État effectif, calculé à la lecture : une demande `PENDING` dont le délai est
 * passé, ou un accès `APPROVED` dont la durée est écoulée, sont `EXPIRED` ;
 * un accès `APPROVED` quitté définitivement par l'opérateur est `ENDED`.
 */
export const SupportAccessState = z.enum([
    'PENDING',
    'APPROVED',
    'EXPIRED',
    'ENDED',
    'DECLINED',
    'CANCELLED',
    'REVOKED',
]);
export const SupportAccessEndReason = z.enum(['LEFT', 'REVOKED', 'EXPIRED']);
export const SupportAccessActor = z.object({
    userId: Uuid,
    fullName: z.string(),
    email: Email.nullable(),
});
export const SupportAccessRequest = z.object({
    id: Uuid,
    tenantId: Uuid,
    /** Identifiant et nom du tenant cible (utile aux listes de la console). */
    tenantSlug: z.string(),
    tenantName: z.string(),
    requestedBy: SupportAccessActor,
    reason: z.string(),
    requestedMinutes: z.number().int().positive(),
    status: SupportAccessStatus,
    state: SupportAccessState,
    /** Délai de réponse du Owner (demande en attente). */
    requestExpiresAt: IsoDateTime,
    decidedBy: SupportAccessActor.nullable(),
    decidedAt: IsoDateTime.nullable(),
    decisionNote: z.string().nullable(),
    approvedMinutes: z.number().int().positive().nullable(),
    /** Fin de l'accès approuvé (approbation + durée), R-SUP-03. */
    expiresAt: IsoDateTime.nullable(),
    firstEnteredAt: IsoDateTime.nullable(),
    lastSeenAt: IsoDateTime.nullable(),
    /** Requêtes servies en mode support (compteur, D-H45). */
    requestsServed: z.number().int().nonnegative(),
    endedAt: IsoDateTime.nullable(),
    endReason: SupportAccessEndReason.nullable(),
    version: Version,
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
/** Liste de demandes, avec le plafond de durée de la plateforme (`SUPPORT_ACCESS_MAX_MINUTES`). */
export const SupportAccessRequestList = z.object({
    requests: z.array(SupportAccessRequest),
    maxMinutes: z.number().int().positive(),
});
/** Console : nouvelle demande d'accès à un tenant. */
export const CreateSupportAccessRequest = z.object({
    reason: z.string().trim().min(10).max(1000),
    requestedMinutes: z
        .number()
        .int()
        .min(5)
        .max(24 * 60),
});
/** Tenant : approbation (durée ≤ demandée et ≤ plafond ; défaut = demandée). */
export const ApproveSupportAccessRequest = z.object({
    minutes: z
        .number()
        .int()
        .min(5)
        .max(24 * 60)
        .optional(),
    note: z.string().trim().max(500).optional(),
});
/** Tenant : refus ou révocation, avec note facultative. */
export const DecideSupportAccessRequest = z.object({
    note: z.string().trim().max(500).optional(),
});
/** Console : sortie du tenant ; `end` clôt l'accès (plus de retour possible). */
export const LeaveSupportAccessRequest = z.object({
    end: z.boolean().default(false),
});
/** Session en mode support (`CurrentUser.supportAccess`). */
export const SupportAccessSession = z.object({
    requestId: Uuid,
    tenantId: Uuid,
    tenantName: z.string(),
    reason: z.string(),
    expiresAt: IsoDateTime,
});
/** Réponse à l'entrée ou à la sortie : la session a été rotée, nouveau jeton CSRF. */
export const SupportAccessSessionResponse = z.object({
    supportAccess: SupportAccessSession.nullable(),
    csrfToken: z.string(),
});
//# sourceMappingURL=support.js.map